import type { ReactNode } from "react";
import { cn, tv } from "tailwind-variants";
import { Heading } from "#/_packages/umbrella/components/ui/primitives/heading.tsx";
import { ResponsiveImage } from "#/_packages/umbrella/components/ui/responsive-image";
import type {
	AspectRatio,
	HeadingLevel,
	ImageMedia,
	Link,
} from "#/_packages/umbrella/types";

export type ImageAndTextProps = {
	id?: string;
	image?: ImageMedia;
	imageAlignLeft?: boolean;
	aspectRatio?: AspectRatio;
	headingLabel?: string;
	heading?: string;
	headingLevel?: HeadingLevel;
	headingAppearance?: string | null;
	preamble?: string;
	body?: ReactNode;
	links?: readonly Link[];
	animationScrollType?: string | null;
	alignmentSelfVertical?: "start" | "center" | "end" | "stretch" | null;
	hoverItemClasses?: string | null;
	hoverMediaClasses?: string | null;
	className?: string;
};

const imageAndText = tv({
	slots: {
		image: "figure-img w-full object-cover",
	},
	variants: {
		aspectRatio: {
			none: { image: "" },
			"1:1": { image: "aspect-square" },
			"4:3": { image: "aspect-[4/3]" },
			"3:2": { image: "aspect-[3/2]" },
			"16:9": { image: "aspect-video" },
			"21:9": { image: "aspect-[21/9]" },
		},
	},
});

export function ImageAndText({
	id,
	image,
	imageAlignLeft = true,
	aspectRatio = "16:9",
	headingLabel,
	heading,
	headingLevel = 2,
	headingAppearance,
	preamble,
	body,
	links = [],
	animationScrollType,
	alignmentSelfVertical,
	hoverItemClasses,
	hoverMediaClasses,
	className,
}: ImageAndTextProps) {
	const { image: imageClass } = imageAndText({ aspectRatio });
	const alignmentSelfVerticalClass =
		alignmentSelfVertical && alignmentSelfVertical !== "stretch"
			? {
					start: "self-start",
					center: "self-center",
					end: "self-end",
				}[alignmentSelfVertical]
			: undefined;

	return (
		<section
			id={id}
			className={cn(
				"block__image-text w-full",
				hoverItemClasses,
				animationScrollType !== "none" && animationScrollType,
				alignmentSelfVerticalClass,
				className,
			)}
		>
			<div className="grid gap-7 md:grid-cols-2">
				{image ? (
					<div
						className={cn(
							"g-col-full md:g-col-1",
							!imageAlignLeft && "md:order-last",
						)}
					>
						<figure className={cn("figure", hoverMediaClasses)}>
							<ResponsiveImage image={image} className={imageClass()} />
							{image.caption ? (
								<figcaption className="figure-caption">
									{image.caption}
								</figcaption>
							) : null}
						</figure>
					</div>
				) : null}

				<div className={cn("g-col-full md:g-col-1")}>
					<div className="mb-0-last flex flex-col justify-center md:h-full">
						{headingLabel || heading ? (
							<header>
								{headingLabel ? (
									<span className="kicker">{headingLabel}</span>
								) : null}
								{heading ? (
									<Heading
										level={headingLevel}
										display={headingLevel}
										className={headingAppearance ?? undefined}
									>
										{heading}
									</Heading>
								) : null}
							</header>
						) : null}

						{preamble ? <p className="lead">{preamble}</p> : null}

						{body}

						{links.length > 0 ? (
							<div className="image-text-footer">
								{links.map((link) => (
									<a
										key={link.id}
										href={link.href}
										target={link.target}
										rel={
											link.target === "_blank"
												? "noopener noreferrer"
												: undefined
										}
									>
										{link.label}
									</a>
								))}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</section>
	);
}
